import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Quote, Instagram, Facebook, Camera } from "lucide-react";
import FanSubmissionModal from "@/components/FanSubmissionModal";

const Media = () => {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data));
  }, []);

  const photos = items.filter((i) => i.type === "photo");
  const videos = items.filter((i) => i.type === "video");
  const press = items.filter((i) => i.type === "press");

  return (
    <div data-testid="media-page" className="min-h-screen">
      <section className="pt-20 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="eyebrow fade-up">Media + Press</p>
            <h1 className="font-heading text-6xl md:text-8xl uppercase leading-[0.9] mt-5 fade-up-1" data-testid="media-title">
              Watch + Listen
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-neutral-300 fade-up-2">
              Live clips, photos from the Southbay and what the press has been saying.
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="btn-retro-solid fade-up-3 self-start md:self-end"
            data-testid="media-share-cta"
          >
            <Camera size={14} className="mr-3"/> Share Your Photos / Videos
          </button>
        </div>
      </section>

      {/* Photos */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          {(() => {
            const isFan = (item) => !item.url || !item.url.startsWith("/assets/band/");
            const main = photos
              .filter((p) => !isFan(p))
              .sort((a, b) => a.url.localeCompare(b.url));
            const fan = photos.filter(isFan); // already DESC by created_at from API

            return (
              <>
                <div className="flex items-baseline justify-between mb-8">
                  <h2 className="font-heading text-3xl md:text-4xl uppercase">The Band</h2>
                  <p className="eyebrow">{main.length} shots</p>
                </div>
                {main.length === 0 ? (
                  <p className="text-neutral-400 font-mono text-sm">No photos yet.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="media-grid-main">
                    {main.map((p, idx) => (
                      <a key={p.id} href={p.url} target="_blank" rel="noreferrer"
                         className={`group relative overflow-hidden ${idx === 0 ? "col-span-2 row-span-2 md:col-span-2 md:row-span-2 aspect-square" : "aspect-square"}`}
                         data-testid={`media-photo-${p.id}`}>
                        <img src={p.url} alt={p.title} className="w-full h-full object-cover vintage-img group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-4">
                          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white opacity-0 group-hover:opacity-100 transition-opacity">{p.title}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {fan.length > 0 && (
                  <>
                    {/* Divider */}
                    <div className="mt-20 mb-10 flex items-center gap-6" data-testid="fan-uploads-divider">
                      <div className="h-px flex-1 bg-white/15" />
                      <div className="text-center shrink-0">
                        <p className="eyebrow">{fan.length} from the crowd</p>
                        <h3 className="font-heading text-3xl md:text-4xl uppercase mt-2 leading-tight">
                          Straight From<br className="md:hidden" /> The Crowd
                        </h3>
                      </div>
                      <div className="h-px flex-1 bg-white/15" />
                    </div>
                    <p className="text-center text-sm text-neutral-400 max-w-xl mx-auto mb-10">
                      Shots and clips from fans and the band's inner circle. Latest first.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="media-grid-fan">
                      {fan.map((p) => (
                        <a key={p.id} href={p.url} target="_blank" rel="noreferrer"
                           className="group relative overflow-hidden aspect-square"
                           data-testid={`media-photo-${p.id}`}>
                          <img src={p.url} alt={p.title} className="w-full h-full object-cover vintage-img group-hover:scale-105 transition-transform duration-700" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex flex-col justify-end p-4">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300 opacity-0 group-hover:opacity-100 transition-opacity">
                              {p.description || "Fan upload"}
                            </span>
                            <span className="font-mono text-xs uppercase tracking-[0.15em] text-white opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                              {p.title}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* Videos */}
      {videos.length > 0 && (
        <section className="py-16 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <h2 className="font-heading text-3xl md:text-4xl uppercase mb-8">Live Clips</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {videos.map((v) => (
                <div key={v.id} className="aspect-video bg-neutral-900 border border-white/10" data-testid={`media-video-${v.id}`}>
                  <iframe src={v.url} className="w-full h-full" title={v.title} allowFullScreen frameBorder="0" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Press */}
      {press.length > 0 && (
        <section className="py-16 border-t border-white/10 bg-neutral-950">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <h2 className="font-heading text-3xl md:text-4xl uppercase mb-10">Press + Testimonials</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {press.map((q) => (
                <div key={q.id} className="border border-white/15 p-8" data-testid={`media-press-${q.id}`}>
                  <Quote className="text-white/40 mb-4" size={28} />
                  <p className="font-heading text-2xl leading-snug">{q.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Follow strip */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 text-center">
          <p className="eyebrow">Follow along</p>
          <h2 className="font-heading text-4xl md:text-5xl uppercase mt-3">More on socials</h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href="https://instagram.com/iloveretrofitmusic" target="_blank" rel="noreferrer" className="btn-retro" data-testid="social-instagram"><Instagram size={14} className="mr-2"/> Instagram</a>
            <a href="https://www.facebook.com/retrofitmusic" target="_blank" rel="noreferrer" className="btn-retro" data-testid="social-facebook"><Facebook size={14} className="mr-2"/> Facebook</a>
          </div>
        </div>
      </section>

      <FanSubmissionModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Media;
